/**
 * Microsoft Graph upload helper — uploads a file into a project's SharePoint Job
 * Folder using the SIGNED-IN USER's delegated token. The tracker only ever
 * stores the resulting link + metadata; SharePoint is the source of truth.
 *
 * Flow:
 *   1. Resolve the Job Folder URL → a driveItem (the folder) via the /shares API.
 *   2. Ensure an app-managed subfolder ("RFIs" / "Submittals") under it, creating
 *      it if missing.
 *   3. Upload the file — a simple PUT for small files (<4 MB), or a chunked
 *      upload session for larger ones.
 *
 * Admin consent for the Graph scope (Files.ReadWrite.All, delegated) may still be
 * pending. When a token can't be acquired the caller's getToken throws, and we
 * surface that as a GraphConsentError so the UI can show a friendly "still being
 * set up" message instead of crashing.
 */

const GRAPH = 'https://graph.microsoft.com/v1.0';
const SIMPLE_UPLOAD_MAX = 4 * 1024 * 1024; // 4 MB — Graph's simple-PUT ceiling
/**
 * Upload-session chunks must be a multiple of 320 KiB (327,680 bytes), all
 * but the last — Graph can fail a transfer whose fragments aren't. 4 MiB is
 * NOT one (12.8 × 320 KiB), though an earlier note here said it was; 16 ×
 * 320 KiB = 5 MiB is (Codex on #593, found with video clips).
 */
export const CHUNK_ALIGN = 320 * 1024;
export const CHUNK_SIZE = 16 * CHUNK_ALIGN;

/**
 * Thrown when a Graph access token can't be acquired — typically because admin
 * consent for the delegated scope is still pending. The UI treats this distinctly
 * from a generic upload failure.
 */
export class GraphConsentError extends Error {
  constructor(message = 'Graph consent required') {
    super(message);
    this.name = 'GraphConsentError';
  }
}

export interface UploadToJobFolderArgs {
  /** Returns a Graph access token (or throws/empty → GraphConsentError). */
  getToken: () => Promise<string>;
  /** The project-root SharePoint folder URL (job_folder_url). */
  jobFolderUrl: string;
  /** App-managed subfolder to upload into ("RFIs" | "Submittals"). */
  subfolder: string;
  /** Final file name (already built to the naming convention). */
  fileName: string;
  /** The file to upload. */
  file: File;
  /**
   * What SharePoint does when `fileName` already exists there. Default
   * 'rename' (keep both), which is what every caller wants for a fresh file.
   * 'replace' is for re-sending the SAME file under the SAME unique name —
   * the site photo composer's retry after an upload whose reply was lost —
   * so the retry overwrites its own first copy instead of leaving it behind
   * (review of #560). Only pass it with a name nothing else could have.
   */
  conflictBehavior?: 'rename' | 'replace';
  /**
   * Bytes sent so far, after each chunk of a large upload (and once at the
   * end of a small one) — for a progress bar on a long upload like a video.
   */
  onProgress?: (sent: number, total: number) => void;
}

export interface UploadResult {
  /** SharePoint driveItem id — version-chain registration key. */
  id: string;
  webUrl: string;
  name: string;
  size: number;
}

/**
 * Encode a sharing URL into the Graph "u!" share id: base64 of the URL with
 * +→-, /→_, and trailing = stripped. (Per Graph /shares encoding rules.)
 */
function encodeShareId(url: string): string {
  // btoa needs a binary string; encodeURIComponent→unescape yields UTF-8 bytes.
  const b64 = btoa(unescape(encodeURIComponent(url)));
  return 'u!' + b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Resolve a medium thumbnail image URL for each SharePoint file webUrl, via the
 * /shares API → driveItem → thumbnails. Returns a map of webUrl → thumbnail URL
 * (a short-lived, pre-authenticated image URL usable directly as an <img src>).
 *
 * Best-effort: each lookup is wrapped in try/catch and simply omitted from the
 * map on any failure (consent pending, no thumbnail yet, etc.) — it never
 * throws, so the caller can render placeholders for the misses.
 *
 * At most THUMBNAIL_CONCURRENCY lookups are in flight at once. A page of the
 * site photo thread can carry hundreds of photos (20 posts × up to 30), and
 * starting one Graph request per photo simultaneously is how a busy thread
 * gets throttled and ends up showing placeholders (review of #560).
 */
export const THUMBNAIL_CONCURRENCY = 6;

export async function resolveThumbnails(
  webUrls: string[],
  getToken: () => Promise<string>,
  size: 'medium' | 'large' = 'medium',
): Promise<Record<string, string>> {
  const map: Record<string, string> = {};
  if (webUrls.length === 0) return map;

  let token: string;
  try {
    token = await getToken();
  } catch {
    // No token (consent pending) → no thumbnails; render placeholders.
    return map;
  }
  if (!token) return map;

  const lookup = async (webUrl: string) => {
    try {
      const res = await fetch(
        `${GRAPH}/shares/${encodeShareId(webUrl)}/driveItem/thumbnails/0/${size}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return;
      const body = await res.json();
      const url: string | undefined = body?.url;
      if (url) map[webUrl] = url;
    } catch {
      /* omit this url from the map */
    }
  };

  // A small worker pool: each worker takes the next link until none are left.
  const queue = [...new Set(webUrls)];
  const worker = async () => {
    for (let next = queue.shift(); next !== undefined; next = queue.shift()) await lookup(next);
  };
  await Promise.all(Array.from({ length: Math.min(THUMBNAIL_CONCURRENCY, queue.length) }, worker));

  return map;
}

/**
 * A short-lived, pre-authenticated URL that downloads the ORIGINAL file (the
 * driveItem's `@microsoft.graph.downloadUrl`), or null on any failure — the
 * caller falls back to opening the SharePoint page, which has its own download.
 * `signal` lets a caller that no longer needs it (the photo viewer, after a
 * swipe) cancel the lookup; an aborted lookup is null like any other failure.
 */
export async function resolveDownloadUrl(
  webUrl: string,
  getToken: () => Promise<string>,
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const token = await getToken();
    if (!token || signal?.aborted) return null;
    const res = await fetch(`${GRAPH}/shares/${encodeShareId(webUrl)}/driveItem`, {
      headers: { Authorization: `Bearer ${token}` },
      ...(signal ? { signal } : {}),
    });
    if (!res.ok) return null;
    const body = await res.json();
    const url: unknown = body?.['@microsoft.graph.downloadUrl'];
    return typeof url === 'string' ? url : null;
  } catch {
    return null;
  }
}

/** Pull Graph's error message out of a non-2xx response body (best-effort). */
async function graphErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    const msg = body?.error?.message ?? body?.message;
    if (msg) return `${fallback}: ${msg}`;
  } catch {
    /* non-JSON body — fall through */
  }
  return `${fallback} (HTTP ${res.status})`;
}

/** Resolve the Job Folder URL to its drive + item ids via the /shares API. */
export async function resolveFolder(
  token: string,
  jobFolderUrl: string,
): Promise<{ driveId: string; folderId: string }> {
  const shareId = encodeShareId(jobFolderUrl);
  let res = await fetch(
    `${GRAPH}/shares/${shareId}/driveItem?$select=id,parentReference,webUrl`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    throw new Error(await graphErrorMessage(res, "Couldn't open the Job Folder in SharePoint"));
  }
  const item = await res.json();
  // The returned driveItem IS the folder → use its own id as the folder id, and
  // its parentReference.driveId as the drive. (Both live on the same drive.)
  let driveId: string | undefined = item?.parentReference?.driveId;
  const folderId: string | undefined = item?.id;

  // parentReference.driveId is normally present; if not, re-fetch explicitly.
  if (!driveId) {
    res = await fetch(
      `${GRAPH}/shares/${shareId}/driveItem?$select=id,parentReference,webUrl`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) {
      const again = await res.json();
      driveId = again?.parentReference?.driveId;
    }
  }

  if (!driveId || !folderId) {
    throw new Error("Couldn't resolve the SharePoint drive for the Job Folder.");
  }
  return { driveId, folderId };
}

/**
 * The id of `subfolder` directly under `folderId`, creating it if it is not
 * there — looked up BY PATH, not by listing.
 *
 * ⚠️ WHY NOT LIST THE CHILDREN. The helper this replaced listed the Job
 * Folder's children and read only the first page Graph returns, so on a folder
 * with more children than one page it missed a subfolder that exists, tried to
 * create it, got a 409, re-read the same first page, and gave up — every
 * upload on that job failed for good. (Codex, P2 on #559.) A by-path GET has
 * no pages: 200 is the folder, 404 is "not there yet". It is also how
 * SharePoint itself resolves the name, case and all.
 */
async function ensureSubfolderByPath(
  token: string,
  driveId: string,
  folderId: string,
  subfolder: string,
): Promise<string> {
  const byPath = `${GRAPH}/drives/${driveId}/items/${folderId}:/${encodeURIComponent(subfolder)}`;
  const find = async (): Promise<string | undefined> => {
    const res = await fetch(`${byPath}?$select=id,folder`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error(await graphErrorMessage(res, `Couldn't open the "${subfolder}" folder`));
    const item = await res.json();
    if (!item?.folder) throw new Error(`"${subfolder}" in the Job Folder is a file, not a folder.`);
    return item.id as string;
  };

  const existing = await find();
  if (existing) return existing;

  const createRes = await fetch(`${GRAPH}/drives/${driveId}/items/${folderId}/children`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: subfolder, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' }),
  });
  if (createRes.ok) return (await createRes.json()).id as string;
  // 409: somebody created it between the lookup and the create.
  if (createRes.status === 409) {
    const raced = await find();
    if (raced) return raced;
  }
  throw new Error(await graphErrorMessage(createRes, `Couldn't create the "${subfolder}" subfolder`));
}

/** Simple PUT for files under the 4 MB ceiling. */
async function uploadSimple(
  token: string,
  driveId: string,
  subfolderId: string,
  fileName: string,
  file: File,
  conflictBehavior: 'rename' | 'replace' = 'rename',
): Promise<UploadResult> {
  const res = await fetch(
    `${GRAPH}/drives/${driveId}/items/${subfolderId}:/${encodeURIComponent(fileName)}:/content?@microsoft.graph.conflictBehavior=${conflictBehavior}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        // Explicit Content-Type so SharePoint treats this as a binary content
        // write. Without it, a file whose Blob type is empty sends no
        // Content-Type and SharePoint rejects it with "Entity only allows
        // writes with a JSON Content-Type header".
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    },
  );
  if (!res.ok) {
    throw new Error(await graphErrorMessage(res, 'Upload to SharePoint failed'));
  }
  const item = await res.json();
  return { id: item.id, webUrl: item.webUrl, name: item.name, size: item.size ?? file.size };
}

/** Chunked upload session for files at/over the 4 MB ceiling. */
async function uploadLarge(
  token: string,
  driveId: string,
  subfolderId: string,
  fileName: string,
  file: File,
  conflictBehavior: 'rename' | 'replace' = 'rename',
  onProgress?: (sent: number, total: number) => void,
): Promise<UploadResult> {
  const sessionRes = await fetch(
    `${GRAPH}/drives/${driveId}/items/${subfolderId}:/${encodeURIComponent(fileName)}:/createUploadSession`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ item: { '@microsoft.graph.conflictBehavior': conflictBehavior } }),
    },
  );
  if (!sessionRes.ok) {
    throw new Error(await graphErrorMessage(sessionRes, "Couldn't start the SharePoint upload"));
  }
  const { uploadUrl } = await sessionRes.json();
  if (!uploadUrl) throw new Error("SharePoint didn't return an upload URL.");

  const total = file.size;
  let start = 0;
  // PUT the file in ≤4 MB chunks; the final 2xx carries the created driveItem.
  // The upload session URL is pre-authenticated — no Authorization header needed.
  while (start < total) {
    const end = Math.min(start + CHUNK_SIZE, total);
    const chunk = file.slice(start, end);
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Length': String(end - start),
        'Content-Range': `bytes ${start}-${end - 1}/${total}`,
      },
      body: chunk,
    });

    if (res.status === 200 || res.status === 201) {
      const item = await res.json();
      onProgress?.(total, total);
      return { id: item.id, webUrl: item.webUrl, name: item.name, size: item.size ?? total };
    }
    if (res.status !== 202) {
      throw new Error(await graphErrorMessage(res, 'Chunked upload to SharePoint failed'));
    }
    // 202 Accepted — server may report nextExpectedRanges; we just advance.
    start = end;
    onProgress?.(start, total);
  }

  throw new Error('Upload completed without a final SharePoint response.');
}

/**
 * Upload `file` into `subfolder` under the project's Job Folder. Returns the
 * created driveItem's webUrl/name/size. Throws GraphConsentError if a token
 * can't be acquired, or a descriptive Error on any Graph failure.
 */
export async function uploadToJobFolder(args: UploadToJobFolderArgs): Promise<UploadResult> {
  const { getToken, jobFolderUrl, subfolder, fileName, file, conflictBehavior = 'rename', onProgress } = args;

  let token: string;
  try {
    token = await getToken();
  } catch (e) {
    if (e instanceof GraphConsentError) throw e;
    throw new GraphConsentError(e instanceof Error ? e.message : 'Graph consent required');
  }
  if (!token) throw new GraphConsentError('No Graph access token was returned.');

  const { driveId, folderId } = await resolveFolder(token, jobFolderUrl);
  const subfolderId = await ensureSubfolderByPath(token, driveId, folderId, subfolder);

  if (file.size >= SIMPLE_UPLOAD_MAX) return uploadLarge(token, driveId, subfolderId, fileName, file, conflictBehavior, onProgress);
  const done = await uploadSimple(token, driveId, subfolderId, fileName, file, conflictBehavior);
  onProgress?.(file.size, file.size);
  return done;
}

/**
 * Upload a small file into `subfolder` under the Job Folder ONCE: if a file of
 * that exact name is already there, return it instead of uploading a copy.
 *
 * ⚠️ WHY NOT `uploadToJobFolder`. That one renames on a clash ("x 1.pdf"),
 * which is right for a new revision and wrong for a record. Filing a Travel
 * Acknowledgment is upload-then-record: if the Project Tracker fails to record
 * the upload, filing again must find the file it already put there, not add a
 * second one. So this uses `conflictBehavior=fail` and, on Graph's 409, reads
 * the existing item by path. The caller's name must therefore be deterministic.
 *
 * Simple PUT only — the caller's files are a few KB; anything at or over the
 * 4 MB simple-upload ceiling is refused rather than silently renamed by the
 * chunked path.
 */
export async function fileOnceToJobFolder(
  args: UploadToJobFolderArgs,
): Promise<UploadResult & { alreadyThere: boolean }> {
  const { getToken, jobFolderUrl, subfolder, fileName, file } = args;
  if (file.size >= SIMPLE_UPLOAD_MAX) throw new Error(`"${fileName}" is too large to file this way.`);

  let token: string;
  try {
    token = await getToken();
  } catch (e) {
    if (e instanceof GraphConsentError) throw e;
    throw new GraphConsentError(e instanceof Error ? e.message : 'Graph consent required');
  }
  if (!token) throw new GraphConsentError('No Graph access token was returned.');

  const { driveId, folderId } = await resolveFolder(token, jobFolderUrl);
  // Creates the subfolder the first time anybody files on this job.
  const subfolderId = await ensureSubfolderByPath(token, driveId, folderId, subfolder);
  /**
   * ⚠️ TWO URL SHAPES. Path addressing closes the path with `:` only when a
   * segment follows it (`…:/name:/content`); the item's own metadata is
   * `…:/name` with no closing colon. (Codex, P1 on #559.)
   */
  const itemUrl = `${GRAPH}/drives/${driveId}/items/${subfolderId}:/${encodeURIComponent(fileName)}`;

  const res = await fetch(`${itemUrl}:/content?@microsoft.graph.conflictBehavior=fail`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (res.ok) {
    const item = await res.json();
    return { id: item.id, webUrl: item.webUrl, name: item.name, size: item.size ?? file.size, alreadyThere: false };
  }
  if (res.status === 409) {
    const existing = await fetch(itemUrl, { headers: { Authorization: `Bearer ${token}` } });
    if (existing.ok) {
      const item = await existing.json();
      return { id: item.id, webUrl: item.webUrl, name: item.name, size: item.size ?? file.size, alreadyThere: true };
    }
    throw new Error(await graphErrorMessage(existing, `"${fileName}" is already in SharePoint but couldn't be read`));
  }
  throw new Error(await graphErrorMessage(res, 'Filing to SharePoint failed'));
}

/**
 * Download a file the Project Tracker filed in `subfolder` of the Job Folder
 * (a weekly report, to read it again), with the reader's own SharePoint access.
 *
 * By its own link first (`webUrl`, through the /shares API, which finds it
 * even after a rename or a move), then by name in the subfolder, when the
 * job has a Job Folder linked to look in. Throws GraphConsentError without a
 * token, or an Error naming the file when it can't be found or read, or is
 * larger than `maxBytes`: the file may have been replaced in SharePoint since
 * the Tracker put it there, by anyone who can write to the folder, so its
 * size is not taken on trust.
 */
export async function downloadFromJobFolder(args: {
  getToken: () => Promise<string>;
  /** Only for the fallback by name: without one, the file's own link must find it. */
  jobFolderUrl: string | null;
  subfolder: string;
  fileName: string;
  webUrl?: string | null;
  maxBytes?: number;
}): Promise<ArrayBuffer> {
  const { getToken, jobFolderUrl, subfolder, fileName, webUrl, maxBytes = Number.POSITIVE_INFINITY } = args;
  const tooLarge = () => new Error(`"${fileName}" is larger than ${Math.round(maxBytes / 1024 / 1024)} MB, too large to be the report.`);
  /** The body, refused by its declared size before it is read, and by its real size after. */
  const body = async (res: Response) => {
    if (Number(res.headers.get('content-length') ?? 0) > maxBytes) throw tooLarge();
    const bytes = await res.arrayBuffer();
    if (bytes.byteLength > maxBytes) throw tooLarge();
    return bytes;
  };
  let token: string;
  try {
    token = await getToken();
  } catch (e) {
    if (e instanceof GraphConsentError) throw e;
    throw new GraphConsentError(e instanceof Error ? e.message : 'Graph consent required');
  }
  if (!token) throw new GraphConsentError('No Graph access token was returned.');

  if (webUrl) {
    const direct = await resolveDownloadUrl(webUrl, async () => token);
    if (direct) {
      // Pre-authenticated and short-lived: fetched WITHOUT an Authorization header.
      const res = await fetch(direct);
      if (res.ok) return body(res);
    }
  }
  if (!jobFolderUrl) {
    throw new Error(`"${fileName}" couldn't be found by its own link, and the job has no Job Folder linked to look in. Link it on the Documents tab.`);
  }
  const { driveId, folderId } = await resolveFolder(token, jobFolderUrl);
  const path = [subfolder, fileName].map(encodeURIComponent).join('/');
  // Graph answers /content with a redirect to a pre-authenticated URL; the
  // browser drops the Authorization header on that cross-origin hop.
  const res = await fetch(`${GRAPH}/drives/${driveId}/items/${folderId}:/${path}:/content`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) throw new Error(`"${fileName}" is no longer in the Job Folder's "${subfolder}" folder.`);
  if (!res.ok) throw new Error(await graphErrorMessage(res, `Couldn't download "${fileName}" from SharePoint`));
  return body(res);
}

/**
 * Upload into a SPECIFIC folder (by drive/item id) — the Documents tab lets
 * the user pick any real folder, unlike the app-managed-subfolder flow above.
 * Same size-based simple/chunked switch; caller owns token acquisition.
 */
export async function uploadToFolderId(args: {
  token: string; driveId: string; folderId: string; fileName: string; file: File;
}): Promise<UploadResult> {
  const { token, driveId, folderId, fileName, file } = args;
  return file.size < SIMPLE_UPLOAD_MAX
    ? uploadSimple(token, driveId, folderId, fileName, file)
    : uploadLarge(token, driveId, folderId, fileName, file);
}

// ─── Schedule sync: pull the newest MSPDI .xml from the Job Folder ───────────

export interface FetchLatestScheduleXmlArgs {
  /** Returns a Graph access token (or throws/empty → GraphConsentError). */
  getToken: () => Promise<string>;
  /** The project-root SharePoint folder URL (job_folder_url). */
  jobFolderUrl: string;
}

export interface ScheduleXmlResult {
  fileName: string;
  file: File;
}

/** Minimal driveItem child shape we care about when scanning for .xml exports. */
interface DriveChild {
  id?: string;
  name?: string;
  file?: unknown; // present (an object) on files; absent on folders
  lastModifiedDateTime?: string;
  '@microsoft.graph.downloadUrl'?: string;
}

/**
 * Find the most-recently-modified MSPDI `.xml` directly inside the project's Job
 * Folder and download its bytes, returning a `File` ready for
 * `scheduleApi.import`. SharePoint stays the source of truth — nothing is
 * stored; we re-scan for the newest export on every sync.
 *
 * Throws GraphConsentError if a token can't be acquired (admin consent for the
 * delegated scope still pending), or a descriptive Error when the folder can't
 * be opened or contains no `.xml`.
 */
export async function fetchLatestScheduleXml(
  args: FetchLatestScheduleXmlArgs,
): Promise<ScheduleXmlResult> {
  const { getToken, jobFolderUrl } = args;

  let token: string;
  try {
    token = await getToken();
  } catch (e) {
    if (e instanceof GraphConsentError) throw e;
    throw new GraphConsentError(e instanceof Error ? e.message : 'Graph consent required');
  }
  if (!token) throw new GraphConsentError('No Graph access token was returned.');

  const { driveId, folderId } = await resolveFolder(token, jobFolderUrl);

  // List the folder's children, asking Graph for the pre-authenticated
  // download URL alongside name/file/lastModified so we can fetch bytes without
  // a second round-trip.
  const res = await fetch(
    `${GRAPH}/drives/${driveId}/items/${folderId}/children` +
      `?$select=id,name,file,lastModifiedDateTime,@microsoft.graph.downloadUrl&$top=200`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    throw new Error(await graphErrorMessage(res, "Couldn't read the Job Folder contents"));
  }
  const body = await res.json();
  const children: DriveChild[] = body?.value ?? [];

  // Files (not folders) whose name ends in .xml (case-insensitive).
  const xmls = children.filter(
    (c) => c.file != null && typeof c.name === 'string' && /\.xml$/i.test(c.name),
  );
  if (xmls.length === 0) {
    throw new Error(
      "No .xml schedule found in the Job Folder — save an MS Project 'Save As → XML' export there.",
    );
  }

  // Most recently modified wins.
  xmls.sort(
    (a, b) =>
      new Date(b.lastModifiedDateTime ?? 0).getTime() -
      new Date(a.lastModifiedDateTime ?? 0).getTime(),
  );
  const newest = xmls[0];
  const fileName = newest.name ?? 'schedule.xml';

  // @microsoft.graph.downloadUrl is NOT reliably returned in a $select'ed
  // children listing. If it's absent, re-fetch the chosen item on its own — a
  // single-item GET returns the pre-authenticated download URL by default.
  let downloadUrl: string | undefined = newest['@microsoft.graph.downloadUrl'];
  if (!downloadUrl && newest.id) {
    const itemRes = await fetch(`${GRAPH}/drives/${driveId}/items/${newest.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (itemRes.ok) {
      downloadUrl = (await itemRes.json())?.['@microsoft.graph.downloadUrl'];
    }
  }

  let blob: Blob;
  if (downloadUrl) {
    // Pre-authenticated + short-lived — fetch it WITHOUT an Authorization header.
    const dl = await fetch(downloadUrl);
    if (!dl.ok) throw new Error(`Couldn't download "${fileName}" from SharePoint (HTTP ${dl.status}).`);
    blob = await dl.blob();
  } else {
    // Final fallback: stream the /content endpoint. Graph 302-redirects to a
    // pre-authed URL; the browser drops the Authorization header on that
    // cross-origin hop, so the storage URL accepts it.
    const dl = await fetch(`${GRAPH}/drives/${driveId}/items/${newest.id}/content`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!dl.ok) throw new Error(`Couldn't download "${fileName}" from SharePoint (HTTP ${dl.status}).`);
    blob = await dl.blob();
  }
  const file = new File([blob], fileName, { type: 'text/xml' });
  return { fileName, file };
}

/** The convention below, as shown to people who upload. */
export const ATTACHMENT_NAME_HINT = 'JobNo_DocNo_ShortDesc_STATUS_YYYY-MM-DD';

/**
 * Build the SharePoint filename to the E&W convention:
 *   {jobNumber}_{docNumber}_{ShortDesc}_{STATUS}_{YYYY-MM-DD}.{ext}
 * e.g. 23-1771_RFI-001_SlabPenetration_OPEN_2026-06-24.pdf
 * shortDescSource is reduced to alphanumerics only and capped (~30 chars).
 */
export function buildAttachmentName(args: {
  jobNumber: string | null | undefined;
  docNumber: string | null | undefined;
  shortDescSource: string | null | undefined;
  status: string | null | undefined;
  ext: string;
}): string {
  const { jobNumber, docNumber, shortDescSource, status, ext } = args;
  const shortDesc = (shortDescSource ?? '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 30) || 'doc';
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const date = `${yyyy}-${mm}-${dd}`;
  const job = (jobNumber && jobNumber.trim()) || 'JOB';
  const doc = (docNumber && docNumber.trim()) || 'entity';
  const stat = (status ?? '').toUpperCase() || 'STATUS';
  return `${job}_${doc}_${shortDesc}_${stat}_${date}.${ext}`;
}
